import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'
import type { SingleResponse } from '@/types/api'

/** Endereço inline (não relation). Cidade vem expandida em `city` quando o show é carregado. */
export interface SubscriberAddress {
  postalCode: string | null
  address:    string | null
  number:     string | null
  complement: string | null
  district:   string | null
  city?: {
    id: string
    city: string
    stateId?: string
    state?: { id: string; state: string; abbreviation: string } | null
  } | null
}

export interface SubscriberBillingByMail {
  // A saída da API camelCaseia chaves aninhadas: yes_or_no → yesOrNo.
  yesOrNo?: 'SIM' | 'NÃO' | null
  seasonality?: 'MENSAL' | 'SEMESTRAL' | 'ANUAL' | null
}

export interface Subscriber {
  id: string
  code: string
  parentId: string | null
  name: string
  socialName: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  businessPhone: string | null
  cpf: string | null
  rg: string | null
  rnm: string | null
  birthDate: string | null
  type: 'TITULAR' | 'DEPENDENTE'
  gender: string | null
  paymentMethod: string
  status: 'ATIVO' | 'INATIVO'
  observations: string | null
  membershipCardNumber: string | null
  dueDay: number
  billingByMail: SubscriberBillingByMail | null
  // FKs cruas para prefill confiável
  planId: string | null
  managerId: string | null
  maritalStatusId: string | null
  degreeRelationshipId: string | null
  discountCouponId: string | null
  companyId: string | null
  companyBranchId: string | null
  contractPath: string | null
  asaasId?: string | null
  asaasSignatureId?: string | null
  /** Endereço inline. Vem como objeto sempre (campos podem ser null individualmente). */
  address?: SubscriberAddress
  plan?: { id: string; name: string } | null
  manager?: { id: string; name: string } | null
  company?: { id: string; name: string } | null
  companyBranch?: { id: string; systemName: string | null; corporateName: string | null } | null
  discountCoupon?: { id: string; code: string; formatted?: { value?: string } } | null
  /** Lista de dependentes carregada no `show`. */
  dependents?: Subscriber[]
  /** Titular (carregado para dependentes na listagem). */
  parent?: Subscriber | null
  formatted?: {
    cpf?: string
    phone?: string
    mobile?: string
    birthDate?: string
    membershipCardNumber?: string
    postalCode?: string
    createdAt?: string
    updatedAt?: string
    /** Momento em que passou a INATIVO (só preenchido se estiver inativo). */
    inactivatedAt?: string | null
  }
}

export interface EligiblePlan {
  id: string
  name: string
  modality: string | null
  formatted: { price: string }
}

export interface DependentPayload {
  id?: string
  name: string
  cpf?: string | null
  rg?: string | null
  birth_date?: string | null
  gender?: 'MASCULINO' | 'FEMININO' | 'OUTRO' | null
  marital_status_id?: string | null
  degree_relationship_id?: string | null
}

/** Grupo de usuários elegíveis a responsável, agrupados por tipo no select. */
export interface SubscriberManagerGroup {
  type: 'DIRETOR' | 'SUPERVISOR' | 'AFILIADO' | 'VENDEDOR'
  users: Array<{ id: string; name: string }>
}

const base = createCrudService<Subscriber>('/commercial/subscribers')

export const SubscribersService = {
  ...base,

  /** Usuários elegíveis a Responsável (Diretor, Supervisor, Afiliado, Vendedor). */
  async managerOptions(): Promise<SubscriberManagerGroup[]> {
    const r = await api.get<{ data: SubscriberManagerGroup[] }>('/commercial/subscribers/manager-options')
    return r.data.data
  },

  /** Upload de contrato PDF (substitui o anterior). Só em edição. */
  async uploadContract(subscriberId: string, file: File): Promise<Subscriber> {
    const fd = new FormData()
    fd.append('contract', file)
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/contract`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return r.data.data
  },

  /** Informações de comissionamento (responsável direto + árvore + empresa). */
  async commissionInfo(subscriberId: string): Promise<import('@/components/commercial/CommissionInfoPanel').CommissionInfo> {
    const r = await api.get<{ data: import('@/components/commercial/CommissionInfoPanel').CommissionInfo }>(`/commercial/subscribers/${subscriberId}/commission-info`)
    return r.data.data
  },

  /** 2ª via do cartão: gera um NOVO número de carteirinha para o assinante. */
  /** Muda o status e devolve quantos dependentes foram junto na cascata. */
  async setStatusCascading(id: string, status: 'ATIVO' | 'INATIVO'): Promise<SubscriberStatusCascade> {
    const r = await api.put<{ data: unknown; meta?: { cascade?: SubscriberStatusCascade } }>(
      `/commercial/subscribers/${id}/status`, { status },
    )
    return r.data.meta?.cascade ?? { holder: 1, dependents: 0 }
  },

  async reissueCard(subscriberId: string): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/reissue-membership-card`)
    return r.data.data
  },

  /** Planos elegíveis ao titular (respeita graus dos dependentes; opcional por empresa). */
  async eligiblePlans(subscriberId: string, companyId?: string): Promise<EligiblePlan[]> {
    const r = await api.get<{ data: EligiblePlan[] }>(`/commercial/subscribers/${subscriberId}/eligible-plans`, {
      params: { company_id: companyId || undefined },
    })
    return r.data.data
  },

  /** Desvincula o titular da empresa, assumindo um plano individual. */
  async unlinkCompany(subscriberId: string, planId: string): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/unlink-company`, { plan_id: planId })
    return r.data.data
  },

  /** Troca/vincula o titular a uma empresa, assumindo um plano dela. */
  async changeCompany(subscriberId: string, companyId: string, planId: string): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/change-company`, { company_id: companyId, plan_id: planId })
    return r.data.data
  },

  /** Gera faturas no intervalo mês/ano (ignora competências já existentes). */
  async generateInvoicesRange(subscriberId: string, payload: { start_month: number; start_year: number; end_month: number; end_year: number }): Promise<{ created: number; skipped: number; failed?: number; failures?: Record<string, string> }> {
    const r = await api.post<SingleResponse<{ created: number; skipped: number; failed?: number; failures?: Record<string, string> }>>(`/commercial/subscribers/${subscriberId}/generate-invoices-range`, payload)
    return r.data.data
  },

  /** Converte um titular em dependente de outro titular. */
  async makeDependent(id: string, payload: { target_holder_id: string; degree_relationship_id: string }): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${id}/make-dependent`, payload)
    return r.data.data
  },

  /** Converte um dependente em titular (pede plano + responsável). */
  async makeHolder(id: string, payload: { plan_id: string; manager_id: string; payment_method?: string }): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${id}/make-holder`, payload)
    return r.data.data
  },

  /** Muda para cartão de crédito (cria assinatura no Asaas). Dados do cartão não são gravados. */
  async switchToCreditCard(subscriberId: string, card: { card_holder_name: string; card_number: string; expiry: string; cvv: string }): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/switch-to-credit-card`, card)
    return r.data.data
  },

  /** Muda para boleto (cancela a assinatura no Asaas). */
  async switchToBoleto(subscriberId: string): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/switch-to-boleto`)
    return r.data.data
  },

  async addDependent(subscriberId: string, payload: DependentPayload): Promise<Subscriber> {
    const r = await api.post<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/dependents`, payload)
    return r.data.data
  },
  async updateDependent(subscriberId: string, depId: string, payload: Partial<DependentPayload>): Promise<Subscriber> {
    const r = await api.put<SingleResponse<Subscriber>>(`/commercial/subscribers/${subscriberId}/dependents/${depId}`, payload)
    return r.data.data
  },
  async removeDependent(subscriberId: string, depId: string): Promise<void> {
    await api.delete(`/commercial/subscribers/${subscriberId}/dependents/${depId}`)
  },

  /** Relatório de maioridade — paginado (mesma forma do list). */
  async reportComingOfAge(params: Record<string, unknown> = {}) {
    const r = await api.get<import('@/types/api').PaginatedResponse<Subscriber>>(
      '/commercial/subscribers/report-coming-of-age',
      { params },
    )
    return r.data
  },

  /** Relatório por localidade (resumo + drill-down estado→cidade). */
  async reportLocation(stateId?: string) {
    const r = await api.get<{
      data: LocationRow[]
      level: 'state' | 'city'
      totals: LocationTotals
    }>('/commercial/subscribers/report-location', { params: { state_id: stateId || undefined } })
    return r.data
  },
}

/** Quantos dependentes acompanharam a mudança de status do titular. */
export interface SubscriberStatusCascade {
  holder: number
  dependents: number
}

export interface LocationRow {
  id: string
  name: string
  abbreviation: string | null
  holders: number
  dependents: number
  companyHolders: number
  individualHolders: number
  companies: number
  total: number
}

export interface LocationTotals {
  holders: number
  dependents: number
  companyHolders: number
  individualHolders: number
  companies: number
  total: number
}
